import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowTopRightOnSquareIcon, Battery50Icon, BoltIcon, CpuChipIcon, HomeIcon } from '@heroicons/react/24/outline'
import { fetchProducts } from '../../requests'
import { ProductImage } from '../ProductImage'
import { obrazekKonstrukcji } from '../../lib/konstrukcja'
import { preferencjaKlienta } from '../../lib/rynek'

/**
 * Sprzęt w zleceniu jako kafelki (uwagi przeglądu właściciela E8, 2026-10-25): moduły (moc, liczba), falownik,
 * magazyn (pojemność) i dach (pokrycie, zdjęcie konstrukcji jak w Projekcie 3D i kreatorze). Gdy teczka zlecenia zna
 * model z bazy (`teczka.sprzet.*.model_id` albo `opis.<rodzaj>_model_id`), kafelek ma zdjęcie produktu, a nazwa otwiera
 * stronę modelu w nowej karcie (prośba właściciela 2026-10-30). Starsze zlecenia dostają id z backendu (`rynek/modele.py`).
 */

const ENDPOINT = { panel: 'get_panel_model', inwerter: 'get_inverter_model', magazyn: 'get_magazine_model' }
// Wartości `opis.dach` z formularza (`DACHY_RYNKU`) na konstrukcję z `lib/konstrukcja` (zdjęcie).
const DACH_NA_KONSTRUKCJE = { 'Dachówka': 'dachowka_ceramiczna', Blacha: 'blachodachowka', Papa: 'membrana_papa', Grunt: 'grunt' }

export const slugModelu = (nazwa) => String(nazwa || 'model').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/ł/g, 'l').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'model'

export const adresModeluZlecenia = (kategoria, model) => (model?.model_id ? `/model/${kategoria}/${slugModelu(model.nazwa)}-${model.model_id}` : null)

// Pole obrazka w rogu kafelka (uwagi przeglądu F1): stały rozmiar, obrazek mieści się w nim w całości; bez obrazka ikona.
const POLE = 'flex h-14 w-14 flex-none items-center justify-center overflow-hidden rounded-xl'

const IkonaPola = ({ Ikona }) => (
  <span aria-hidden="true" className={`${POLE} bg-gray-100 text-gray-700`}><Ikona className="h-5 w-5" /></span>
)

const ObrazModelu = ({ kategoria, id, alt, Ikona }) => {
  const [src, setSrc] = useState(null)
  useEffect(() => {
    if (!id) return undefined
    let anulowane = false
    Promise.resolve(fetchProducts(`${ENDPOINT[kategoria]}/${id}`)).then((d) => { if (!anulowane && d && !d.error) setSrc(d.img_url || null) }).catch(() => {})
    return () => { anulowane = true }
  }, [kategoria, id])
  if (!src) return <IkonaPola Ikona={Ikona} />
  return (
    <span className={`${POLE} border border-gray-100 p-1`} style={{ background: '#FFFFFF' }}>
      <ProductImage src={src} productType={kategoria} alt={alt} className="max-h-full max-w-full object-contain" />
    </span>
  )
}

const NazwaModelu = ({ nazwa, link }) => (link ? (
  <Link to={link} target="_blank" rel="noopener" className="font-semibold text-blue-700 no-underline hover:underline">
    {nazwa}<ArrowTopRightOnSquareIcon className="ml-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />
  </Link>
) : nazwa)

// `wartosc` to liczba (moc, pojemność) albo, gdy jej nie ma, sama nazwa; wtedy nazwa nie powtarza się pod spodem.
const Kafel = ({ Ikona, obraz, etykieta, wartosc, sztuki, nazwa, opis, link, woli }) => {
  const nazwaPodSpodem = nazwa && wartosc !== nazwa
  return (
    <li className="flex min-w-0 flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-3.5">
      <div className="flex items-center gap-2.5">
        {obraz || <IkonaPola Ikona={Ikona} />}
        <span className="min-w-0 text-[13px] font-semibold text-gray-600">{etykieta}</span>
      </div>
      {wartosc === nazwa
        ? <p className="m-0 text-[15px] font-bold leading-snug text-gray-900"><NazwaModelu nazwa={nazwa} link={link} /></p>
        : <p className="m-0 text-lg font-bold leading-tight tabular-nums text-gray-900">{wartosc}</p>}
      {(sztuki || nazwaPodSpodem) && (
        <p title={nazwaPodSpodem ? nazwa : undefined} className="m-0 line-clamp-4 text-[12.5px] leading-snug text-gray-600">
          {sztuki}{sztuki && nazwaPodSpodem ? ' · ' : ''}
          {nazwaPodSpodem && <NazwaModelu nazwa={nazwa} link={link} />}
        </p>
      )}
      {opis && <p className="m-0 text-[12.5px] leading-snug text-gray-600">{opis}</p>}
      {woli && <p className="m-0 text-[12.5px] leading-snug text-gray-600"><span className="font-semibold text-gray-800">Klient woli:</span> {woli}</p>}
    </li>
  )
}

const liczbaPl = (v) => String(v).replace('.', ',')

// „4 × SE-F5 Pro-C”: liczba sztuk magazynu z `lib/rynek.ogloszenieZZestawu` osobno od nazwy, która jest linkiem.
const ILOSC = /^(\d+)\s*×\s*/
const sztukiZNazwy = (t) => String(t || '').match(ILOSC)?.[1] || null
const bezSztuk = (t) => String(t || '').replace(ILOSC, '')

// Model z bazy bez teczki: z opisu zlecenia (`opis.<rodzaj>_model_id`, zestaw dobrany z odpowiedzi doboru, G5).
const zOpisu = (opis, rodzaj) => (opis[`${rodzaj}_model_id`] ? { model_id: opis[`${rodzaj}_model_id`], nazwa: bezSztuk(opis[rodzaj]) } : undefined)

const SprzetZlecenia = ({ o }) => {
  const opis = o.opis || {}
  const zTeczki = o.teczka?.sprzet || {}
  const sprzet = {
    moduly: zTeczki.moduly || zOpisu(opis, 'moduly'),
    falownik: zTeczki.falownik || zOpisu(opis, 'falownik'),
    magazyn: zTeczki.magazyn || zOpisu(opis, 'magazyn'),
  }
  const kafle = []
  // Preferencje klienta bez modelu (H6): „Klient woli: Huawei, hybrydowy”; bez liczby w kafelku to wartość kafelka.
  const woli = { moduly: preferencjaKlienta(opis, 'moduly'), falownik: preferencjaKlienta(opis, 'falownik'), magazyn: preferencjaKlienta(opis, 'magazyn') }
  const DO_WYBORU = 'Do wyboru wykonawcy'
  if (opis.kwp || opis.moduly || sprzet.moduly || woli.moduly) {
    const m = sprzet.moduly
    kafle.push({ klucz: 'moduly', Ikona: BoltIcon, etykieta: 'Moduły', woli: woli.moduly,
      nazwa: opis.moduly || m?.nazwa, wartosc: opis.kwp ? `${liczbaPl(opis.kwp)} kWp` : (opis.moduly || m?.nazwa || DO_WYBORU),
      sztuki: opis.liczba_modulow || m?.liczba ? `${opis.liczba_modulow || m.liczba} szt.` : null,
      link: adresModeluZlecenia('panel', m), obraz: m?.model_id && <ObrazModelu kategoria="panel" id={m.model_id} alt={m.nazwa} Ikona={BoltIcon} /> })
  }
  if (opis.falownik || sprzet.falownik || woli.falownik) {
    const f = sprzet.falownik
    // H7: moc falownika (kW) jak moc modułów i pojemność magazynu; pełna nazwa pod spodem.
    const kw = f?.mocKw || opis.falownik_kw
    kafle.push({ klucz: 'falownik', Ikona: CpuChipIcon, etykieta: 'Falownik', woli: woli.falownik,
      nazwa: opis.falownik || f?.nazwa, wartosc: kw ? `${liczbaPl(kw)} kW` : (opis.falownik || f?.nazwa || DO_WYBORU),
      opis: !kw && f?.hybrydowy ? 'hybrydowy' : '',
      link: adresModeluZlecenia('inwerter', f), obraz: f?.model_id && <ObrazModelu kategoria="inwerter" id={f.model_id} alt={f.nazwa} Ikona={CpuChipIcon} /> })
  }
  if (opis.magazyn_kwh || (opis.magazyn && opis.magazyn !== 'bez magazynu') || sprzet.magazyn || woli.magazyn) {
    const g = sprzet.magazyn
    const kwh = opis.magazyn_kwh || g?.kwh
    const nazwaG = bezSztuk(opis.magazyn) || g?.nazwa
    const sztukiG = sztukiZNazwy(opis.magazyn)
    kafle.push({ klucz: 'magazyn', Ikona: Battery50Icon, etykieta: 'Magazyn energii', woli: woli.magazyn,
      nazwa: nazwaG, wartosc: kwh ? `${liczbaPl(kwh)} kWh` : (nazwaG || DO_WYBORU), sztuki: sztukiG ? `${sztukiG} szt.` : null,
      link: adresModeluZlecenia('magazyn', g), obraz: g?.model_id && <ObrazModelu kategoria="magazyn" id={g.model_id} alt={g.nazwa} Ikona={Battery50Icon} /> })
  }
  if (opis.dach) {
    const foto = obrazekKonstrukcji(DACH_NA_KONSTRUKCJE[opis.dach])
    kafle.push({ klucz: 'dach', Ikona: HomeIcon, etykieta: opis.dach === 'Grunt' ? 'Montaż' : 'Dach', wartosc: opis.dach,
      opis: opis.dach === 'Grunt' ? 'Konstrukcja na gruncie' : 'Rodzaj pokrycia',
      obraz: foto && <span className={`${POLE} border border-gray-100`}><img src={foto} alt="" className="h-full w-full object-cover" /></span> })
  }
  if (!kafle.length) return null
  return (
    <ul aria-label="Sprzęt w zleceniu" className="m-0 mt-4 grid list-none grid-cols-1 gap-2.5 p-0 min-[420px]:grid-cols-2 lg:grid-cols-4">
      {kafle.map(({ klucz, ...k }) => <Kafel key={klucz} {...k} />)}
    </ul>
  )
}

export default SprzetZlecenia
